import _ from 'lodash'

import Logger from '@/logger'
import ServiceFactory from '@/services/service-factory'
import SchemeServicingManager from '@/scheme-servicing-manager'

import Store from '@/store'
import { MUTATIONS } from '@/store/mutations'

import AppError from '@/models/app-error'
import Project from '@/models/project'
import Property from '@/models/property'
import Renovation from '@/models/renovation'
import RenovationDataSubmission from '@/models/renovation-data-submission'
import RenovationOperationResult from '@/models/renovation-operation-result'

const loggingCategory = 'Renovation Management'

/*
 * The Renovations Manager deals with interactions with the improvements service and core builder business logic.
 */
const RenovationsManager = {

    /**
     * Creates or Updates the linked renovation data for the project in state.
     * @async
     * @param {?number} servicedSchemeId An optional serviced scheme identifier to set manually. Auto-detection will be used if not provided.
     * @param {boolean} suppressError If set, will suppress error logging.
     * @returns {Promise<RenovationOperationResult>} A result.
     * @throws {Error}
     */
    async createOrUpdateAsync({ servicedSchemeId = null, suppressError = false } = { servicedSchemeId: null, suppressError: false }) {
        Logger.trackMethod('createOrUpdateAsync', 'RenovationsManager', { servicedSchemeId, suppressError })
        const standardCoreErrorMessage = 'Unable to upsert renovation data'
        let operationState, presentationScheme

        /**
         * Throw a formatted error and log appropriately.
         * @param {string} coreMessage
         * @param {Error} err
         * @throws {Error}
         */
        const throwAndLogError = function(coreMessage = standardCoreErrorMessage, err = null) {
            const msg = err && err.message ? err.message : err
            const errMsg = msg ? `${ coreMessage }: ${ msg }` : coreMessage

            if (!suppressError) {
                if (operationState)
                    Logger.logObject(operationState, errMsg, loggingCategory)
                else
                    Logger.log(errMsg, loggingCategory)

                Store.commit(MUTATIONS.logAppError,
                    new AppError(coreMessage !== standardCoreErrorMessage
                        ? `Unable to submit your plan (${ coreMessage })`
                        : 'Unable to submit your plan', true))
            }

            throw new Error(errMsg)
        }

        if (!Store.state.property)
            throwAndLogError('Unable to save a plan without a property specified')

        if (!Store.state.improvements.currentProject || !Store.state.improvements.currentProject.initialised)
            throwAndLogError('Unable to save a plan without a project to base it on')

        if (!Store.state.improvements.currentProject.name)
            throwAndLogError('Unable to save a plan without a name')

        try {
            presentationScheme = await SchemeServicingManager.getPresentationSchemeAsync()
        } catch (err) {
            throwAndLogError('Unable to get the active presentation scheme')
        }

        if (!servicedSchemeId) {
            try {
                const servicedScheme = await SchemeServicingManager.getDefaultServicedSchemeAsync()
                servicedSchemeId = servicedScheme !== null ? servicedScheme.id : null
            } catch (err) {
                throwAndLogError('Unable to get the serviced scheme')
            }
        }

        try {
            const addressId = Store.state.property.id
            const project = Project.ensure(Store.state.improvements.currentProject)
            const data = new RenovationDataSubmission(project, presentationScheme, servicedSchemeId)
            Logger.logObject({
                addressId,
                vuexSource: Store.state.improvements.currentProject,
                project,
                submission: data,
            }, 'Renovation Data Submission readied', loggingCategory)

            operationState = await ServiceFactory.renovation()
                .upsertRenovationAsync(addressId, data)

            if (!operationState.succeeded) {
                if (operationState.error.includes('Cannot insert duplicate key in object \'dbo.RenovationNotes\'')) {
                    Logger.warn(operationState.data.message, 'Ignored attempt to save twice to the database')
                    return RenovationOperationResult.ignored('Ignored attempt to save twice to the database')
                }

                operationState.throw()
                return RenovationOperationResult.failed(operationState.error)
            }

            if (operationState.statusCode === 202) {
                Logger.warn(operationState.data.message, loggingCategory)
                return RenovationOperationResult.ignored(operationState.data.message)
            }

            return RenovationOperationResult.succeeded(Renovation.ensure(operationState.data))
        } catch (err) {
            throwAndLogError(standardCoreErrorMessage, err)
        }
    },

    /**
     * Loads the renovation data.
     * @async
     * @param {string} name
     * @param {boolean} suppressError
     * @param {boolean} allowNull
     * @returns {Promise<Renovation>} A renovation.
     * @throws {Error}
     */
    async loadAsync(name, { suppressError = false, allowNull = false } = { suppressError: false, allowNull: false }) {
        Logger.trackMethod('loadAsync', 'RenovationsManager', { name, suppressError, allowNull })

        if (!Store.state.property)
            throw new Error('Unable to load a renovation as there is no Property in state at this time.')

        const property = Property.ensure(_.cloneDeep(Store.state.property))
        let operationState

        try {
            operationState = await ServiceFactory.renovation().loadRenovationAsync(property.id, name)

            if (!operationState.succeeded) {
                if (!allowNull) operationState.throw()
                return null
            }

            const renovation = Renovation.ensure(operationState.data)
            property.appendRenovation(renovation)
            await SchemeServicingManager.loadLinkedSchemeDataAsync(property)

            return renovation
        } catch (err) {
            if (!suppressError) {
                Logger.logObject(operationState, `Unable to load renovation: ${ err.message }`, loggingCategory)
                Store.commit(MUTATIONS.logAppError, new AppError(`Unable to load your previously saved plan`, true))
            }
            throw err
        }
    },

    /**
     * Submits the current project linked renovation, updating the status.
     * @async
     * @param {boolean} suppressError
     * @returns {Promise<RenovationOperationResult>} A result.
     */
    async submitAsync({ suppressError = false } = { suppressError: false }) {
        Logger.trackMethod('submitAsync', 'RenovationsManager', { suppressError })
        Store.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectStatus, 'submitted')
        return await RenovationsManager.createOrUpdateAsync({ suppressError: suppressError })
    },

    /**
     * Creates or Updates the renovation currently in planning.
     * @async
     * @param {boolean} suppressError
     * @return {Promise<Renovation>} The renovation, updated.
     */
    async upsertInPlanningAsync({ suppressError = false } = { suppressError: false }) {
        Logger.trackMethod('upsertInPlanningAsync', 'RenovationsManager', { suppressError })
        const createRenovationResult = await RenovationsManager.createOrUpdateAsync({ suppressError: suppressError })
        return createRenovationResult.data
    },
}

export default RenovationsManager
